/* ==========================================================================
 * veluna-bonus-gift.js  (v2 — pattern-matched + universal coverage)
 *
 * For ANY subscription product on the store, attach a free PDF gift line
 * (compare $47 -> FREE) keyed off the product handle's ritual family.
 *
 *   Family pattern             Gift                              Variant ID
 *   handle includes mind-calm    -> Mind Calm Reset Guide        43673764954190
 *   handle includes deep-restore -> Deep Restore Sleep Guide     43673764986958
 *   handle includes sacred-soothe-> Sacred Soothe Sleep Guide    43673765052494
 *   handle includes circadian-reset -> Circadian Reset Sleep Guide 43673765118030
 *   anything else (solo SKUs)  -> Sleep Foundations Guide        43673805586510
 *
 * Reconciler:
 *   - Runs after every /cart/* POST and on page load.
 *   - One gift per unique subscribed parent product handle.
 *   - Adds the right gift family for that product.
 *   - Removes orphaned gifts when the parent subscription is removed.
 *   - Marked X-Veluna-Bonus: 1 to skip self-recursion.
 * ========================================================================== */
(function () {
  'use strict';

  var GIFT_FAMILIES = [
    { match: 'mind-calm',       variantId: 43673764954190, title: 'Mind Calm Reset Guide (PDF)' },
    { match: 'deep-restore',    variantId: 43673764986958, title: 'Deep Restore Sleep Guide (PDF)' },
    { match: 'sacred-soothe',   variantId: 43673765052494, title: 'Sacred Soothe Sleep Guide (PDF)' },
    { match: 'circadian-reset', variantId: 43673765118030, title: 'Circadian Reset Sleep Guide (PDF)' },
    { match: '__default__',     variantId: 43673805586510, title: 'Sleep Foundations Guide (PDF)' }
  ];

  function giftFor(parentHandle) {
    if (!parentHandle) return null;
    for (var i = 0; i < GIFT_FAMILIES.length - 1; i++) {
      if (parentHandle.indexOf(GIFT_FAMILIES[i].match) !== -1) return GIFT_FAMILIES[i];
    }
    return GIFT_FAMILIES[GIFT_FAMILIES.length - 1];
  }

  function isGiftLine(item) {
    return !!(item && item.properties && (item.properties._gift === 'true' || item.properties._gift === true));
  }

  function isSubscription(item) {
    return !!(item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
  }

  function itemHandle(item) {
    return (item && (item.handle || item.product_handle)) || '';
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function addGift(variantId, parentHandle) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Veluna-Bonus': '1' },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity: 1,
          properties: { _gift: 'true', _bonus_for: parentHandle }
        }]
      })
    }).then(function (r) { return r.ok ? r.json() : null; });
  }

  function removeLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Veluna-Bonus': '1' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(function (r) { return r.ok ? r.json() : null; });
  }

  function reconcile() {
    if (window.__VELUNA_BONUS_GIFT_RECONCILING__) return Promise.resolve(null);
    window.__VELUNA_BONUS_GIFT_RECONCILING__ = true;

    return fetchCart().then(function (cart) {
      var items = (cart && cart.items) || [];

      var desiredByParent = {};
      items.forEach(function (it) {
        if (isSubscription(it)) {
          var h = itemHandle(it);
          var g = giftFor(h);
          if (h && g) desiredByParent[h] = g.variantId;
        }
      });

      var existingGiftByParent = {};
      var orphanGifts = [];
      var wrongFamilyGifts = [];
      items.forEach(function (it) {
        if (!isGiftLine(it)) return;
        var parent = it.properties && it.properties._bonus_for;
        var desired = parent ? desiredByParent[parent] : null;
        if (!desired) {
          orphanGifts.push(it);
        } else if (Number(it.variant_id) !== Number(desired)) {
          wrongFamilyGifts.push(it);
        } else {
          existingGiftByParent[parent] = it;
        }
      });

      var removals = orphanGifts.concat(wrongFamilyGifts).map(function (g) { return removeLine(g.key); });

      return Promise.all(removals).then(function () {
        var missing = Object.keys(desiredByParent).filter(function (h) { return !existingGiftByParent[h]; });
        return missing.reduce(function (chain, handle) {
          return chain.then(function () { return addGift(desiredByParent[handle], handle); });
        }, Promise.resolve());
      }).then(fetchCart);
    }).then(function (finalCart) {
      window.__VELUNA_BONUS_GIFT_RECONCILING__ = false;
      try {
        if (finalCart && typeof window.__VELUNA_UPDATE_DRAWER__ === 'function') {
          window.__VELUNA_UPDATE_DRAWER__(finalCart);
        }
        window.dispatchEvent(new CustomEvent('veluna:cart:updated', { detail: { cart: finalCart, source: 'bonus-gift' } }));
      } catch (e) {}
      return finalCart;
    }).catch(function (err) {
      window.__VELUNA_BONUS_GIFT_RECONCILING__ = false;
      console.warn('[VELUNA_BONUS_GIFT] reconcile failed', err);
      return null;
    });
  }

  // Hook fetch to trigger reconcile after any cart mutation
  var originalFetch = window.fetch;
  if (originalFetch && !window.__VELUNA_BONUS_GIFT_FETCH_HOOKED__) {
    window.__VELUNA_BONUS_GIFT_FETCH_HOOKED__ = true;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var method = (init && init.method) || (input && input.method) || 'GET';
      var headers = (init && init.headers) || {};
      var isOurOwn = false;
      try {
        if (headers && typeof headers.get === 'function') {
          isOurOwn = headers.get('X-Veluna-Bonus') === '1';
        } else if (headers && headers['X-Veluna-Bonus'] === '1') {
          isOurOwn = true;
        }
      } catch (e) {}

      var isCartMutation = method && method.toUpperCase() === 'POST' &&
        url && (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1 || url.indexOf('/cart/clear') !== -1);

      var promise = originalFetch.apply(this, arguments);
      if (isCartMutation && !isOurOwn) {
        promise.then(function () { setTimeout(function () { reconcile(); }, 60); }).catch(function () {});
      }
      return promise;
    };
  }

  function reconcileOnLoad() { setTimeout(reconcile, 250); }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    reconcileOnLoad();
  } else {
    window.addEventListener('DOMContentLoaded', reconcileOnLoad);
  }

  // Expose API + perks list (mystery discount removed; clearer copy)
  window.VELUNA_GIFT_FAMILIES = GIFT_FAMILIES;
  window.VelunaBonusGift = {
    reconcile: reconcile,
    isGiftLine: isGiftLine,
    isSubscription: isSubscription,
    giftFor: giftFor,
    PERKS: [
      { icon: '%',  label: 'Save up to 25% off',     detail: 'every recurring order, automatically' },
      { icon: '🚚', label: 'FREE US shipping',       detail: 'on every delivery, every time' },
      { icon: '⏸',  label: 'Skip, pause, or cancel', detail: 'anytime from your account dashboard' }
    ]
  };

  console.log('[VELUNA_BONUS_GIFT] v2 loaded — universal coverage via family patterns');
})();
